import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { 
  getEarlyWarnings, getHazardClusters, getWorkloadForecast, getSPITrends
} from '@/services/api';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus, Target, 
  Brain, BarChart3, PieChart, Activity, Shield, Loader2,
  AlertCircle, CheckCircle2, Info, ArrowRight, RefreshCw,
  Lightbulb, Radar
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartPie, Pie, Cell } from 'recharts';

const SEVERITY_COLORS = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#22c55e'
};

const TREND_ICONS = {
  increasing: TrendingUp,
  decreasing: TrendingDown,
  stable: Minus
};

export default function AnalyticsPage() {
  const { t, i18n } = useTranslation();
  const { api, user } = useAuth();
  const { isRTL } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('warnings');
  
  const [warnings, setWarnings] = useState({ warnings: [], total_count: 0 });
  const [clusters, setClusters] = useState({ clusters: [] });
  const [workload, setWorkload] = useState({ current: {}, forecast: {} });
  const [spiTrends, setSpiTrends] = useState({ spis: [] });

  useEffect(() => {
    if (api) {
      fetchAllData();
    }
  }, [api]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [warningsRes, clustersRes, workloadRes, spiRes] = await Promise.all([
        getEarlyWarnings(api, 90),
        getHazardClusters(api, 2),
        getWorkloadForecast(api, 30),
        getSPITrends(api)
      ]);
      setWarnings(warningsRes.data);
      setClusters(clustersRes.data);
      setWorkload(workloadRes.data);
      setSpiTrends(spiRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  const getWarningIcon = (type) => {
    switch (type) {
      case 'incident_trend': return TrendingUp;
      case 'risk_accumulation': return AlertTriangle;
      case 'spi_alert': return Target;
      case 'spi_declining': return TrendingDown;
      default: return AlertCircle;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className={cn("flex flex-col sm:flex-row justify-between gap-4", isRTL && "sm:flex-row-reverse")}>
        <div>
          <h1 className="text-2xl font-bold text-white font-barlow flex items-center gap-2">
            <Brain className="w-7 h-7 text-violet-400" />
            {t('analytics.title', 'Predictive Analytics')}
          </h1>
          <p className="text-slate-400 mt-1">
            {t('analytics.subtitle', 'AI-powered safety insights and risk forecasting')}
          </p>
        </div>
        <Button 
          onClick={handleRefresh}
          variant="outline"
          className="border-slate-600"
          disabled={refreshing}
        >
          <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
          {t('common.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={cn(
          "bg-slate-800/50 border-slate-700",
          warnings.high_severity > 0 && "border-red-500/50"
        )}>
          <CardContent className="p-4">
            <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
              <div className={cn(
                "p-2 rounded-lg",
                warnings.high_severity > 0 ? "bg-red-900/50" : "bg-slate-700"
              )}>
                <AlertTriangle className={cn(
                  "w-5 h-5",
                  warnings.high_severity > 0 ? "text-red-400" : "text-slate-300"
                )} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{warnings.total_count}</p>
                <p className="text-xs text-slate-400">{t('analytics.warnings', 'Active Warnings')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
              <div className="p-2 bg-violet-900/50 rounded-lg">
                <Radar className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-violet-400">{clusters.clusters?.length || 0}</p>
                <p className="text-xs text-slate-400">{t('analytics.hazardClusters', 'Hazard Clusters')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
              <div className="p-2 bg-amber-900/50 rounded-lg">
                <Activity className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{spiTrends.alerts || 0}</p>
                <p className="text-xs text-slate-400">{t('analytics.spiAlerts', 'SPI Alerts')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className={cn("flex items-center gap-3", isRTL && "flex-row-reverse")}>
              <div className="p-2 bg-emerald-900/50 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-400">{spiTrends.improving || 0}</p>
                <p className="text-xs text-slate-400">{t('analytics.improving', 'Improving SPIs')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-800 w-full md:w-auto">
          <TabsTrigger value="warnings" className="data-[state=active]:bg-slate-700">
            <AlertTriangle className="w-4 h-4 mr-2" />
            {t('analytics.earlyWarnings', 'Early Warnings')}
          </TabsTrigger>
          <TabsTrigger value="clusters" className="data-[state=active]:bg-slate-700">
            <Radar className="w-4 h-4 mr-2" />
            {t('analytics.patterns', 'Patterns')}
          </TabsTrigger>
          <TabsTrigger value="forecast" className="data-[state=active]:bg-slate-700">
            <BarChart3 className="w-4 h-4 mr-2" />
            {t('analytics.forecast', 'Workload Forecast')}
          </TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-slate-700">
            <Activity className="w-4 h-4 mr-2" />
            {t('analytics.spiTrends', 'SPI Trends')}
          </TabsTrigger>
        </TabsList>
        
        <div className="mt-6">
          <TabsContent value="warnings" className="m-0">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  {t('analytics.earlyWarningSignals', 'Early Warning Signals')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {warnings.warnings?.length > 0 ? (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {warnings.warnings.map((warning, idx) => {
                        const Icon = getWarningIcon(warning.type);
                        return (
                          <div 
                            key={idx}
                            className={cn(
                              "p-4 rounded-lg border",
                              warning.severity === 'high' ? "bg-red-950/30 border-red-700" :
                              warning.severity === 'medium' ? "bg-amber-950/30 border-amber-700" :
                              "bg-slate-800 border-slate-700"
                            )}
                          >
                            <div className={cn("flex items-start gap-3", isRTL && "flex-row-reverse")}>
                              <div className={cn(
                                "p-2 rounded-lg",
                                warning.severity === 'high' ? "bg-red-900/50" : "bg-amber-900/50"
                              )}>
                                <Icon className={cn(
                                  "w-5 h-5",
                                  warning.severity === 'high' ? "text-red-400" : "text-amber-400"
                                )} />
                              </div>
                              <div className="flex-1">
                                <div className={cn("flex items-center gap-2 mb-1", isRTL && "flex-row-reverse")}>
                                  <Badge className={cn(
                                    "text-xs",
                                    warning.severity === 'high' ? "bg-red-600" : "bg-amber-600"
                                  )}>
                                    {warning.severity.toUpperCase()}
                                  </Badge>
                                  <span className="text-xs text-slate-500">
                                    {warning.type.replace(/_/g, ' ').toUpperCase()}
                                  </span>
                                </div>
                                <p className="text-white font-medium">{warning.message}</p>
                                {warning.recommendation && (
                                  <div className={cn("flex items-center gap-2 mt-2 text-sm text-slate-400", isRTL && "flex-row-reverse")}>
                                    <Lightbulb className="w-4 h-4 text-amber-400" />
                                    <span>{warning.recommendation}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                    <p className="text-lg font-medium text-emerald-400">
                      {t('analytics.noWarnings', 'No Active Warnings')}
                    </p>
                    <p className="text-sm mt-2">
                      {t('analytics.systemHealthy', 'All safety indicators are within normal parameters')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="clusters" className="m-0">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Radar className="w-5 h-5 text-violet-400" />
                  {t('analytics.hazardPatterns', 'Hazard Patterns & Clusters')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {clusters.clusters?.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {clusters.clusters.map((cluster, idx) => (
                      <div 
                        key={idx}
                        className="p-4 bg-slate-800 rounded-lg border border-slate-700"
                      >
                        <div className={cn("flex items-center justify-between mb-3", isRTL && "flex-row-reverse")}>
                          <div>
                            <Badge className="bg-violet-600">{cluster.category}</Badge>
                            <span className="text-slate-400 text-sm ml-2">@ {cluster.location_type}</span>
                          </div>
                          <span className="text-2xl font-bold text-white">{cluster.count}</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex gap-1">
                            {Object.entries(cluster.severity_distribution || {}).map(([sev, count]) => (
                              count > 0 && (
                                <Badge key={sev} variant="outline" className={cn(
                                  "text-xs",
                                  sev === 'catastrophic' || sev === 'hazardous' ? "border-red-500 text-red-400" :
                                  sev === 'major' ? "border-amber-500 text-amber-400" : "border-slate-500 text-slate-400"
                                )}>
                                  {sev}: {count}
                                </Badge>
                              )
                            ))}
                          </div>
                          <div className={cn("flex items-center gap-2 text-sm text-slate-400 mt-2", isRTL && "flex-row-reverse")}>
                            <Lightbulb className="w-4 h-4 text-amber-400" />
                            <span>{cluster.recommendation}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Info className="w-12 h-12 mx-auto mb-4" />
                    <p>{t('analytics.noClusters', 'No significant hazard clusters detected')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="forecast" className="m-0">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-sky-400" />
                  {t('analytics.workloadForecast', '30-Day Workload Forecast')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {Object.entries(workload.forecast || {}).map(([type, data]) => {
                    const TrendIcon = TREND_ICONS[data.trend] || Minus;
                    const isIncreasing = data.trend === 'increasing';
                    
                    return (
                      <div key={type} className="p-4 bg-slate-800 rounded-lg">
                        <div className={cn("flex items-center justify-between mb-2", isRTL && "flex-row-reverse")}>
                          <span className="text-slate-400 capitalize">{type}</span>
                          <TrendIcon className={cn(
                            "w-4 h-4",
                            isIncreasing ? "text-red-400" : "text-emerald-400"
                          )} />
                        </div>
                        <div className={cn("flex items-baseline gap-2", isRTL && "flex-row-reverse")}>
                          <span className="text-3xl font-bold text-white">{data.current}</span>
                          <ArrowRight className="w-4 h-4 text-slate-500" />
                          <span className={cn(
                            "text-xl font-semibold",
                            isIncreasing ? "text-red-400" : "text-emerald-400"
                          )}>
                            {data.projected_30_days}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Current → 30 days
                        </p>
                        {data.weekly_forecast?.length > 0 && (
                          <div className="mt-3 h-16">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={data.weekly_forecast}>
                                <Area 
                                  type="monotone" 
                                  dataKey="projected_count" 
                                  stroke={isIncreasing ? "#ef4444" : "#22c55e"}
                                  fill={isIncreasing ? "#ef4444" : "#22c55e"}
                                  fillOpacity={0.2}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="trends" className="m-0">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-emerald-400" />
                  {t('analytics.spiTrendAnalysis', 'SPI Trend Analysis')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {spiTrends.spis?.map((spi) => {
                      const TrendIcon = TREND_ICONS[spi.trend?.direction] || Minus;
                      const isAlert = spi.is_alert;
                      
                      return (
                        <div 
                          key={spi.spi_id}
                          className={cn(
                            "p-4 rounded-lg border",
                            isAlert ? "bg-red-950/30 border-red-700" : "bg-slate-800 border-slate-700"
                          )}
                        >
                          <div className={cn("flex items-center justify-between", isRTL && "flex-row-reverse")}>
                            <div className="flex-1">
                              <div className={cn("flex items-center gap-2", isRTL && "flex-row-reverse")}>
                                <span className="font-medium text-white">{spi.name}</span>
                                <Badge variant="outline" className="text-xs border-slate-600">
                                  {spi.spi_number}
                                </Badge>
                                {isAlert && (
                                  <Badge className="bg-red-600 text-xs">ALERT</Badge>
                                )}
                              </div>
                              <div className={cn("flex items-center gap-4 mt-2 text-sm", isRTL && "flex-row-reverse")}>
                                <span className="text-slate-400">
                                  Current: <span className={cn(
                                    "font-medium",
                                    isAlert ? "text-red-400" : "text-white"
                                  )}>{spi.current_value}</span>
                                </span>
                                <span className="text-slate-400">
                                  Target: <span className="text-emerald-400">{spi.target_value}</span>
                                </span>
                                <span className="text-slate-400">
                                  Threshold: <span className="text-amber-400">{spi.alert_threshold}</span>
                                </span>
                              </div>
                            </div>
                            <div className={cn("flex items-center gap-2 ml-4", isRTL && "flex-row-reverse")}>
                              <TrendIcon className={cn(
                                "w-5 h-5",
                                spi.trend?.direction === 'increasing' ? "text-red-400" :
                                spi.trend?.direction === 'decreasing' ? "text-emerald-400" : "text-slate-400"
                              )} />
                              <span className={cn(
                                "text-sm font-medium",
                                spi.trend?.direction === 'increasing' ? "text-red-400" :
                                spi.trend?.direction === 'decreasing' ? "text-emerald-400" : "text-slate-400"
                              )}>
                                {spi.trend?.change_rate > 0 ? '+' : ''}{spi.trend?.change_rate}%
                              </span>
                            </div>
                          </div>
                          {spi.trend?.confidence > 0 && (
                            <div className="mt-2">
                              <div className="w-full bg-slate-700 rounded-full h-1">
                                <div 
                                  className={cn(
                                    "h-1 rounded-full",
                                    spi.current_value >= spi.target_value ? "bg-red-500" : "bg-emerald-500"
                                  )}
                                  style={{ width: `${Math.min(100, (spi.current_value / spi.target_value) * 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
